import {
  AiResponseReport,
  type AiResponseReportFile,
  AiResponseSystem,
  type BatchUpdateAiResponseReportFilesRequest,
} from '@kalos/kalos-rpc';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { OpenAIClientService } from '../../tools/helpers';
import { queryKeys } from './constants';

export const useGetAiResponseReportDataQuery = ({
  reportId,
  enabled = true,
}: {
  reportId: number;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: [queryKeys.aiResponseReport.root, 'data', reportId],
    queryFn: async () => {
      const response = await OpenAIClientService.GetAiResponseReportData(
        AiResponseReport.create({ id: reportId }),
      );
      if (response == null) {
        throw new Error(`GetAiResponseReportData failed for reportId=${reportId}`);
      }
      return response;
    },
    enabled: enabled && reportId > 0,
  });
};

export const useBatchGetAiResponseReportQuery = ({
  aiResponsesId,
  enabled = true,
}: {
  aiResponsesId: number;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: [queryKeys.aiResponseReport.root, 'batch', aiResponsesId],
    queryFn: async () => {
      const response = await OpenAIClientService.BatchGetAiResponseReport(
        AiResponseReport.create({ aiResponsesId }),
      );
      return response;
    },
    enabled: enabled && aiResponsesId > 0,
  });
};

export const useBatchGetAiResponsesSystemQuery = ({
  aiResponsesId,
  enabled = true,
}: {
  aiResponsesId: number;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: [queryKeys.aiResponseReport.root, 'aiResponsesSystem', aiResponsesId],
    queryFn: async () => {
      const response = await OpenAIClientService.BatchGetAiResponsesSystem?.(
        AiResponseSystem.create({ aiResponsesId, withoutLimit: true }),
      );
      return response ?? { results: [], totalCount: 0 };
    },
    enabled: enabled && aiResponsesId > 0,
  });
};

export const useUpdateAiResponseReportMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (req: AiResponseReport) => {
      const response = await OpenAIClientService.UpdateAiResponseReport(req);
      return response;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.aiResponseReport.root, 'data', variables.id],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.aiResponseReport.root, 'batch', variables.aiResponsesId],
      });
    },
  });
};

export const useUpdateAiResponseReportFileMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (req: AiResponseReportFile) => {
      const response = await OpenAIClientService.UpdateAiResponseReportFile(req);
      return response;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.aiResponseReport.root, 'data', variables.aiResponseReportId],
      });
    },
  });
};

export const useBatchUpdateAiResponseReportFilesMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (req: BatchUpdateAiResponseReportFilesRequest) => {
      return OpenAIClientService.BatchUpdateAiResponseReportFiles(req);
    },
    onSuccess: (_, variables) => {
      const reportId = variables.updates?.[0]?.aiResponseReportId;
      if (reportId) {
        queryClient.invalidateQueries({
          queryKey: [queryKeys.aiResponseReport.root, 'data', reportId],
        });
      }
    },
  });
};
